// src/components/ecommerce/CustomerDetailModal.jsx
import React, { useState, useEffect } from 'react';
import { 
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  ShoppingCart,
  DollarSign,
  Star,
  MessageCircle,
  Edit,
  Tag,
  TrendingUp,
  Package,
  Clock,
  Heart,
  Award,
  Eye,
  Send,
  FileText,
  CreditCard,
  Gift,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { ecommerceService } from '../../services/ecommerceService';

export const CustomerDetailModal = ({ customer, storeData, isOpen, onClose, onContact, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    if (isOpen && customer) {
      loadCustomerData();
      setEditData({
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        notes: customer.notes || ''
      });
    }
  }, [isOpen, customer]);

  const loadCustomerData = async () => {
    setLoading(true);
    try {
      const orders = await ecommerceService.getCustomerOrders(customer.id);
      setCustomerOrders(orders);
    } catch (error) {
      console.error('Error loading customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await ecommerceService.updateCustomer(customer.id, editData);
      setEditing(false);
      onUpdate?.();
    } catch (error) {
      console.error('Error updating customer:', error);
    }
  };

  const getCustomerSegment = () => {
    const totalSpent = customer.totalSpent || 0;
    const orderCount = customer.totalOrders || 0;

    if (totalSpent >= 5000 && orderCount >= 10) return { label: 'VIP', color: 'purple', icon: Award };
    if (totalSpent >= 2000 && orderCount >= 5) return { label: 'Loyal', color: 'blue', icon: Star };
    if (orderCount >= 3) return { label: 'Regular', color: 'green', icon: User };
    if (orderCount === 1) return { label: 'New', color: 'yellow', icon: Gift };
    return { label: 'Potential', color: 'gray', icon: User };
  };

  const getOrderStatus = (status) => {
    const statusColors = {
      pending: 'text-yellow-700 bg-yellow-100',
      confirmed: 'text-blue-700 bg-blue-100',
      processing: 'text-purple-700 bg-purple-100',
      shipped: 'text-indigo-700 bg-indigo-100',
      delivered: 'text-green-700 bg-green-100',
      cancelled: 'text-red-700 bg-red-100'
    };
    return statusColors[status] || statusColors.pending;
  };

  const segment = getCustomerSegment();
  const SegmentIcon = segment.icon;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'orders', label: 'Orders', icon: ShoppingCart, badge: customerOrders.length },
    { id: 'activity', label: 'Activity', icon: Clock },
    { id: 'notes', label: 'Notes', icon: FileText }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 bg-gray-200 rounded-full flex items-center justify-center">
              {customer.avatar ? (
                <img
                  src={customer.avatar}
                  alt={customer.name}
                  className="h-12 w-12 rounded-full object-cover"
                />
              ) : (
                <User className="h-6 w-6 text-gray-500" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-gray-900">
                  {customer.name || 'Anonymous Customer'}
                </h2>
                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-${segment.color}-100 text-${segment.color}-800`}>
                  <SegmentIcon className="h-3 w-3" />
                  {segment.label}
                </span>
              </div>
              <p className="text-gray-600">{customer.email}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {customer.phone && (
              <button
                onClick={() => onContact(customer, 'phone')}
                className="p-2 text-gray-400 hover:text-green-600 bg-gray-100 rounded-lg hover:bg-green-50"
                title="Call Customer"
              >
                <Phone className="h-4 w-4" />
              </button>
            )}
            
            {customer.email && (
              <button
                onClick={() => onContact(customer, 'email')}
                className="p-2 text-gray-400 hover:text-blue-600 bg-gray-100 rounded-lg hover:bg-blue-50"
                title="Email Customer"
              >
                <Mail className="h-4 w-4" />
              </button>
            )}

            {customer.phone && storeData.integrations?.whatsapp && (
              <button
                onClick={() => onContact(customer, 'whatsapp')}
                className="p-2 text-gray-400 hover:text-green-600 bg-gray-100 rounded-lg hover:bg-green-50"
                title="WhatsApp"
              >
                <MessageCircle className="h-4 w-4" />
              </button>
            )}
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <div className="flex overflow-x-auto">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 border-b-2 whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="font-medium">{tab.label}</span>
                  {tab.badge !== undefined && tab.badge > 0 && (
                    <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Customer Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <ShoppingCart className="h-8 w-8 text-blue-600" />
                    <div>
                      <p className="text-2xl font-bold text-blue-900">{customer.totalOrders || 0}</p>
                      <p className="text-sm text-blue-700">Total Orders</p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <DollarSign className="h-8 w-8 text-green-600" />
                    <div>
                      <p className="text-2xl font-bold text-green-900">
                        R{(customer.totalSpent || 0).toFixed(2)}
                      </p>
                      <p className="text-sm text-green-700">Total Spent</p>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="h-8 w-8 text-purple-600" />
                    <div>
                      <p className="text-2xl font-bold text-purple-900">
                        R{customer.totalOrders > 0 ? ((customer.totalSpent || 0) / customer.totalOrders).toFixed(2) : '0.00'}
                      </p>
                      <p className="text-sm text-purple-700">Avg Order Value</p>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-8 w-8 text-orange-600" />
                    <div>
                      <p className="text-lg font-bold text-orange-900">
                        {customer.lastOrderDate 
                          ? Math.floor((new Date() - new Date(customer.lastOrderDate)) / (1000 * 60 * 60 * 24))
                          : 'N/A'
                        }
                      </p>
                      <p className="text-sm text-orange-700">Days Since Last Order</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Customer Information</h3>
                    <button
                      onClick={() => setEditing(!editing)}
                      className="p-2 text-gray-400 hover:text-blue-600"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  </div>

                  {editing ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <input
                          type="text"
                          value={editData.name}
                          onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                          type="email"
                          value={editData.email}
                          onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                        <input
                          type="tel"
                          value={editData.phone}
                          onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                        <textarea
                          value={editData.address}
                          onChange={(e) => setEditData(prev => ({ ...prev, address: e.target.value }))}
                          rows="2"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveEdit}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditing(false)}
                          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {customer.email && (
                        <div className="flex items-center gap-3">
                          <Mail className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-900">{customer.email}</span>
                        </div>
                      )}
                      {customer.phone && (
                        <div className="flex items-center gap-3">
                          <Phone className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-900">{customer.phone}</span>
                        </div>
                      )}
                      {customer.address && (
                        <div className="flex items-center gap-3">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-900">{customer.address}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-900">
                          Joined {new Date(customer.createdAt?.toDate?.() || customer.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Recent Orders */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Orders</h3>
                  
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : customerOrders.length > 0 ? (
                    <div className="space-y-3">
                      {customerOrders.slice(0, 5).map(order => (
                        <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{order.orderNumber}</p>
                            <p className="text-sm text-gray-500">
                              {new Date(order.createdAt?.toDate?.() || order.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900">R{(order.total || 0).toFixed(2)}</p>
                            <span className={`text-xs px-2 py-1 rounded-full ${getOrderStatus(order.status)}`}>
                              {order.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-600">No orders yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'orders' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Order History</h3>
              
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : customerOrders.length > 0 ? (
                <div className="space-y-4">
                  {customerOrders.map(order => (
                    <div key={order.id} className="bg-white border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="font-semibold text-gray-900">{order.orderNumber}</h4>
                          <p className="text-sm text-gray-500">
                            {new Date(order.createdAt?.toDate?.() || order.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">R{(order.total || 0).toFixed(2)}</p>
                          <span className={`text-xs px-2 py-1 rounded-full ${getOrderStatus(order.status)}`}>
                            {order.status}
                          </span>
                        </div>
                      </div>
                      
                      {order.items && (
                        <div className="border-t border-gray-200 pt-4">
                          <p className="text-sm text-gray-600 mb-2">Items ({order.items.length}):</p>
                          <div className="space-y-2">
                            {order.items.slice(0, 3).map((item, index) => (
                              <div key={index} className="flex items-center justify-between text-sm">
                                <span className="text-gray-700">{item.name} × {item.quantity}</span>
                                <span className="text-gray-900">R{(item.price * item.quantity).toFixed(2)}</span>
                              </div>
                            ))}
                            {order.items.length > 3 && (
                              <p className="text-xs text-gray-500">+{order.items.length - 3} more items</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <ShoppingCart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
                  <p className="text-gray-600">This customer hasn't placed any orders yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Customer Notes</h3>
                <button
                  onClick={() => setEditing(!editing)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editing ? 'Save Notes' : 'Edit Notes'}
                </button>
              </div>
              
              {editing ? (
                <textarea
                  value={editData.notes}
                  onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                  rows="10"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Add notes about this customer..."
                />
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 min-h-[200px]">
                  {customer.notes ? (
                    <p className="text-gray-700 whitespace-pre-wrap">{customer.notes}</p>
                  ) : (
                    <p className="text-gray-500 italic">No notes added yet</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};